import { useState } from 'react';
import { Button, Spinner, Modal, ModalHeader, ModalBody, Row, Col } from 'reactstrap';
import { Formik, Form } from 'formik';
import {
  FormikInput as Input,
  FormikPasswordInput as PasswordInput,
  FormikCheckboxInput as CheckboxInput,
  useTranslator,
  useToast,
  setAuthCredentials,
  useAppContext,
  getCookie,
} from '@front-activerse/common-components';
import validationSchema from './validationSchema';
import { useOutletContext, useNavigate, Link } from 'react-router-dom';

const SignUpForm = ({
  loading,
  register,
  redirectToPathAfterSuccess,
  action,
  setIsCheckboxChecked,
}) => {
  const { t } = useTranslator();
  const { toastError } = useToast();
  const navigate = useNavigate();
  const [errors, setErrors] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [authToken, setAuthToken] = useState('');

  const { trackFirebaseEvent } = useOutletContext();

  const {
    images: { eye, eyeSlash },
  } = useAppContext();

  const toggle = () => {
    setShowModal(!showModal);
  };

  const initialValues = {
    email: getCookie('onboarding_email') || '',
    password: '',
    acceptConditions: false,
  };

  const handleSubmit = async (values, { resetForm }) => {
    // action sripe means stripe register
    if (action === 'stripe') {
      trackFirebaseEvent('user_action', {
        action: 'tap-stripe_signup',
        location: 'stripe_signup',
        stripe_gdpr: true,
        used_account: 'mail',
      });
    }

    const request = {
      email: values.email,
      password: values.password,
      agreementsTermsAndConditions: true, // required by backend
      agreementsPersonalDataProcessing: true, // required by backend
    };

    try {
      const response = await register(request);
      const { __typename, token, details, existingAccountAutoLogged } = response.data.auth.register;

      if (__typename === 'AuthRegisterSuccess' && !existingAccountAutoLogged) {
        setAuthCredentials(token);
        resetForm();
        navigate(redirectToPathAfterSuccess, { replace: true });
      } else if (__typename === 'AuthRegisterSuccess' && existingAccountAutoLogged) {
        setAuthToken(token);
        setShowModal(true);
      } else if (__typename === 'ValidationException') {
        const errorsArray = [];
        for (let i = 0; i < details.length; i += 1) {
          const error = details[i];
          error.messages.forEach((message) => {
            errorsArray.push(message);
          });
        }
        setErrors(errorsArray);
      }
    } catch (e) {
      toastError();
      throw new Error(`Failed to register, got error: ${e}`);
    }
  };

  const handleClick = () => {
    setAuthCredentials(authToken);
    navigate(redirectToPathAfterSuccess, { replace: true });
  };

  return (
    <>
      <Formik
        validationSchema={validationSchema}
        validateOnChange
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {({ isValid, setFieldTouched }) => {
          return (
            <Form>
              <Input
                name="email"
                type="email"
                label={t('inputLabel.emailAddress')}
                autoComplete="username"
                translateErrMsg
              />
              <PasswordInput
                name="password"
                type="password"
                label={t('inputLabel.password')}
                autoComplete="current-password"
                iconEye={eye}
                iconEyeSlash={eyeSlash}
                translateErrMsg
              />
              <CheckboxInput
                name="acceptConditions"
                translateErrMsg
                onCheckedStatusChange={(e) => setIsCheckboxChecked(e.target.checked)}
              >
                <>
                  {t('app.signUp.termsAccept.checkbox.part1')}{' '}
                  <Link to={'/terms-of-service'}>
                    {t('app.signUp.termsAccept.checkbox.termsOfServiceLink')}
                  </Link>{' '}
                  {t('app.signUp.termsAccept.checkbox.part2')}{' '}
                  <Link to={'/privacy-policy'}>
                    {t('app.signUp.termsAccept.checkbox.privacyPolicyLink')}
                  </Link>{' '}
                  {t('app.signUp.termsAccept.checkbox.part3')}
                </>
              </CheckboxInput>

              {errors.length > 0 && (
                <p className="error my-xl text-center">
                  {errors.map((error, idx) => (
                    <span className="d-block" key={idx}>
                      {error}
                    </span>
                  ))}
                </p>
              )}

              <div
                className="text-center mt-xl"
                onClick={() => {
                  // hack to touched acceptConditions when button is clicked
                  setFieldTouched('acceptConditions', true);
                }}
              >
                <Button
                  type="submit"
                  color="primary"
                  className="w-100"
                  disabled={!isValid || loading}
                  data-test-id="log_in_button"
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" className="me-2">
                        Loading...
                      </Spinner>
                      {t('signUp.signingUp.button')}
                    </>
                  ) : (
                    t('signUp.signUp.button')
                  )}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
      <Modal className="auth-modal" isOpen={showModal} toggle={toggle} centered size="md">
        <ModalHeader toggle={toggle} />
        <ModalBody>
          <p className="large mb-xl">{t('existingAccountPopup.content')}</p>
          <Row>
            <Col>
              <Button color="outline-primary" className="w-100" onClick={toggle}>
                {t('existingAccountPopup.cancelButton')}
              </Button>
            </Col>
            <Col>
              <Button color="primary" className="w-100" onClick={handleClick}>
                {t('existingAccountPopup.loginButton')}
              </Button>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </>
  );
};

export default SignUpForm;
